import { invokeWithRetry } from './common/invokeWithRetry';

export interface CoachAnalyticsParams {
  coachId: string;
  gymId: string;
  startDate: string;
  endDate: string;
}

export interface CoachKpis {
  sessions_coached: number;
  distinct_members: number;
  total_bookings: number;
  avg_fill_rate: number;
  avg_rating: number | null;
  total_ratings: number;
  cancellation_rate: number;
  retention_rate: number;
  estimated_work_hours: number;
}

export interface CoachAnalytics {
  kpis: CoachKpis | null;
  heatmap: Array<{ dow: number; hour: number; bookings: number; sessions: number }>;
  topCourses: Array<{ course_name: string; bookings: number; sessions: number; avg_fill: number }>;
  topMembers: Array<{
    user_id: string;
    first_name: string | null;
    last_name: string | null;
    profile_photo_url: string | null;
    bookings: number;
  }>;
  bookingsTimeline: Array<{ d: string; bookings: number; sessions: number }>;
  ratingsTimeline: Array<{ month: string; avg_rating: number; total_ratings: number }>;
  workSchedule: Array<{ dow: number; first_hour: number; last_hour: number; sessions_count: number; total_hours: number }>;
}

export async function getCoachAnalytics(
  params: CoachAnalyticsParams,
): Promise<CoachAnalytics> {
  // `timeoutMs` élargi : `coach-analytics` enchaîne 7 RPC d'agrégation
  // (`get_coach_kpis`, `get_coach_heatmap`, les deux timelines, l'emploi du
  // temps…) sur la plage de dates demandée. C'est l'appel le plus lourd du
  // dépôt, et il n'avait aucune limite jusqu'ici : le plafond commun de 30 s
  // convertirait une page lente en page en erreur.
  // Pas de multiplication de charge : un timeout n'est plus rejoué
  // (`retryOnTimeout` vaut `false` par défaut).
  const { data, error, authExpired } = await invokeWithRetry(
    'coach-analytics',
    {
      coach_id: params.coachId,
      gym_id: params.gymId,
      start_date: params.startDate,
      end_date: params.endDate,
    },
    2,
    { timeoutMs: 60_000 }
  );
  if (authExpired) {
    return {
      kpis: null, heatmap: [], topCourses: [], topMembers: [],
      bookingsTimeline: [], ratingsTimeline: [], workSchedule: [],
    };
  }
  if (error) throw error;
  return data as CoachAnalytics;
}
